/**
 * 第三方登录：浏览器侧的「授权码 + PKCE」流程，桌面在弹窗里完成、手机整页跳转。UI-agnostic，不依赖 Vue。
 *
 * 本次尝试存 localStorage 而不是 sessionStorage：弹窗是独立窗口，只有 localStorage 两边都读得到。
 *
 * @typedef {{ authorize_url: string, scope: string, pkce: boolean, extra?: Record<string, string>, redirect_path?: string, client_id_param?: string }} OauthAuthorizeConfig
 * @typedef {{ provider: string, state: string, code_verifier: string, redirect_uri: string, return_to: string, popup: boolean }} OauthAttempt
 * @typedef {{ provider: string, code: string, code_verifier?: string, redirect_uri: string, return_to: string, popup: boolean }} OauthCallbackResult
 */

const ATTEMPT_STORAGE_KEY = 'voko_oauth_attempt'

/** 每家平台的授权参数。未写 `redirect_path` 的平台跳回 `/login`。 @type {Record<string, OauthAuthorizeConfig>} */
export const OAUTH_AUTHORIZE = {
  google: {
    authorize_url: 'https://accounts.google.com/o/oauth2/v2/auth',
    scope: 'openid email profile',
    pkce: true,
    extra: { prompt: 'select_account' },
  },
  apple: {
    authorize_url: 'https://appleid.apple.com/auth/authorize',
    scope: 'email',
    pkce: false,
    extra: { response_mode: 'form_post' },
    redirect_path: '/api/sk/auth/oauth/apple/callback',
  },
  x: {
    authorize_url: 'https://x.com/i/oauth2/authorize',
    scope: 'users.read tweet.read users.email',
    pkce: true,
  },
  discord: {
    authorize_url: 'https://discord.com/oauth2/authorize',
    scope: 'identify email',
    pkce: false,
  },
  facebook: {
    authorize_url: 'https://www.facebook.com/v25.0/dialog/oauth',
    scope: 'public_profile,email',
    pkce: false,
  },
  tiktok: {
    authorize_url: 'https://www.tiktok.com/v2/auth/authorize/',
    scope: 'user.info.basic',
    pkce: false,
    client_id_param: 'client_key',
  },
}

/** @param {Uint8Array} bytes @returns {string} */
function base64UrlEncode(bytes) {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return globalThis.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** @returns {string} 32 字节随机 hex，用作 state */
function randomStateHex() {
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(32))
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
}

/** @returns {Promise<{ code_verifier: string, code_challenge: string }>} */
async function createPkcePair() {
  const verifierBytes = globalThis.crypto.getRandomValues(new Uint8Array(32))
  const code_verifier = base64UrlEncode(verifierBytes)
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(code_verifier))
  const code_challenge = base64UrlEncode(new Uint8Array(digest))
  return { code_verifier, code_challenge }
}

/** @param {string} provider @returns {string} */
function redirectUriFor(provider) {
  const config = OAUTH_AUTHORIZE[provider]
  return `${globalThis.location.origin}${config.redirect_path || '/login'}`
}

/**
 * 生成 state / PKCE，把本次尝试存进 localStorage，然后打开授权地址：
 * 传了 `popup`（openOauthPopup 的返回值）就装进弹窗，否则整页跳转。
 * @param {{ provider: string, client_id: string, return_to?: string, popup?: Window|null }} input
 * @returns {Promise<void>}
 */
export async function startOauthLogin({ provider, client_id, return_to, popup }) {
  const config = OAUTH_AUTHORIZE[provider]
  if (!config) throw new Error(`Unsupported oauth provider: ${provider}`)

  const state = randomStateHex()
  const redirect_uri = redirectUriFor(provider)
  const pkce = config.pkce ? await createPkcePair() : null

  /** @type {OauthAttempt} */
  const attempt = { provider, state, code_verifier: pkce?.code_verifier || '', redirect_uri, return_to: return_to || '', popup: Boolean(popup) }
  globalThis.localStorage.setItem(ATTEMPT_STORAGE_KEY, JSON.stringify(attempt))

  const url = new URL(config.authorize_url)
  url.searchParams.set(config.client_id_param || 'client_id', client_id)
  url.searchParams.set('redirect_uri', redirect_uri)
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('scope', config.scope)
  url.searchParams.set('state', state)
  for (const [key, value] of Object.entries(config.extra || {})) url.searchParams.set(key, value)
  if (pkce) {
    url.searchParams.set('code_challenge', pkce.code_challenge)
    url.searchParams.set('code_challenge_method', 'S256')
  }

  if (popup) {
    popup.location.assign(url.toString())
    return
  }
  globalThis.location.assign(url.toString())
}

// 小窗把结果（成功或失败）通知原页面走两条路，任一条到达即算收到（Firebase signInWithPopup 用
// opener.postMessage，Supabase 用 storage 事件做跨窗口同步）：opener 还在就 postMessage，同时写 localStorage 标记兜底。
const POPUP_RESULT_KEY = 'voko_oauth_popup_result'
const POPUP_MESSAGE_TYPE = 'voko-oauth-popup-result'

/** @typedef {{ error: string }} OauthPopupResult 成功时 error 为空串 */

/**
 * 同步打开一个空白弹窗（必须在点击事件里同步调用，否则被浏览器拦截）。被拦截返回 null，调用方退回整页跳转。
 * @returns {Window|null}
 */
export function openOauthPopup() {
  const width = 520
  const height = 680
  const left = Math.max(0, Math.round((globalThis.screen.width - width) / 2))
  const top = Math.max(0, Math.round((globalThis.screen.height - height) / 2))
  return globalThis.open('', 'voko-oauth', `popup,width=${width},height=${height},left=${left},top=${top}`)
}

/**
 * 弹窗里的回调页结束后调用（成功或失败都调）：把结果通知原页面，然后关掉自己。
 * @param {OauthPopupResult} [result]
 */
export function finishOauthInPopup(result = { error: '' }) {
  globalThis.opener?.postMessage({ type: POPUP_MESSAGE_TYPE, error: result.error }, globalThis.location.origin)
  globalThis.localStorage.setItem(POPUP_RESULT_KEY, JSON.stringify({ at: Date.now(), error: result.error }))
  globalThis.close()
}

/**
 * 原页面监听弹窗结果：postMessage 与 storage 事件两路都听，同一次结果只触发一次；用户重试会再触发。返回取消监听的函数。
 * @param {(result: OauthPopupResult) => void} handler
 * @returns {() => void}
 */
export function onOauthPopupResult(handler) {
  let lastAt = 0
  const fire = (at, error) => {
    if (at === lastAt) return
    lastAt = at
    globalThis.localStorage.removeItem(POPUP_RESULT_KEY)
    handler({ error })
  }
  const onMessage = event => {
    if (event.origin !== globalThis.location.origin || event.data?.type !== POPUP_MESSAGE_TYPE) return
    fire(Date.now(), String(event.data.error || ''))
  }
  const onStorage = event => {
    if (event.key !== POPUP_RESULT_KEY || event.newValue === null) return
    const result = JSON.parse(event.newValue)
    fire(result.at, String(result.error || ''))
  }
  globalThis.addEventListener('message', onMessage)
  globalThis.addEventListener('storage', onStorage)
  return () => {
    globalThis.removeEventListener('message', onMessage)
    globalThis.removeEventListener('storage', onStorage)
  }
}

/**
 * 读取回调 query，核对 state，取出并清空存好的尝试。
 * @param {URLSearchParams} searchParams
 * @returns {OauthCallbackResult|{ error: string }|null} 没有 code/error → null
 */
export function takeOauthCallback(searchParams) {
  const code = searchParams.get('code')
  const error = searchParams.get('error')
  if (code === null && error === null) return null

  const raw = globalThis.localStorage.getItem(ATTEMPT_STORAGE_KEY)
  globalThis.localStorage.removeItem(ATTEMPT_STORAGE_KEY)
  /** @type {OauthAttempt|null} */
  const attempt = raw === null ? null : JSON.parse(raw)
  const state = searchParams.get('state') || ''

  if (error !== null) return { error }
  if (attempt === null || state !== attempt.state) return { error: 'state_mismatch' }

  return {
    provider: attempt.provider,
    code: code || '',
    code_verifier: attempt.code_verifier || undefined,
    redirect_uri: attempt.redirect_uri,
    return_to: attempt.return_to || '',
    popup: attempt.popup === true,
  }
}
